import * as React from 'react';
import { cn } from '../../lib/utils';
import { TenantBadge, type TenantTier } from './tenant-badge';
import { ConfidenceBar } from './confidence-bar';
import { LiveBadge } from './live-badge';

export type AlarmTier = 1 | 2 | 3 | 4 | 5;

export interface AlarmCardProps extends React.HTMLAttributes<HTMLButtonElement> {
  tier: AlarmTier;
  siteName: string;
  cameraLabel?: string;
  source: 'panel' | 'ai' | 'nvr' | 'manual';
  description: string;
  receivedAt: Date;
  /** Optional AI verdict score (0..1). */
  confidence?: number;
  /** Pre-alarm clip thumbnail URL (32-frame storyboard or single frame). */
  thumbnailUrl?: string;
  /** Honest empty state when the source alarm has no retained image. */
  emptyThumbnailLabel?: string;
  /** Short operator-visible identifier used to distinguish otherwise identical alarms. */
  referenceLabel?: string;
  /** Tenant chip — keeps cross-SSP scope visible. */
  tenant?: { tier: TenantTier; name: string };
  /** Operator who claimed the alarm, if any. */
  claimedBy?: string;
  /** Selected state — drives visual prominence; announced as `aria-current`. */
  selected?: boolean;
  /**
   * Incident state badge, e.g. "Acknowledged". `muted` dims a card someone is
   * already handling so new alarms stand out.
   */
  state?: { label: string; muted?: boolean };
  /** Translated words on the card; English defaults cover stories and tests. */
  labels?: Partial<AlarmCardLabels>;
  /** Reference time for the elapsed counter (defaults to the render time). */
  now?: Date;
}

export interface AlarmCardLabels {
  tiers: Record<AlarmTier, string>;
  sources: Record<AlarmCardProps['source'], string>;
  claimed: string;
  /** Tooltip on the claimed marker. */
  claimedBy: (name: string) => string;
  /** Badge on the pre-alarm thumbnail. */
  preAlarm: string;
}

const TIER_LABEL: Record<AlarmTier, string> = {
  1: 'T1',
  2: 'T2',
  3: 'T3',
  4: 'T4',
  5: 'T5',
};
const TIER_COLOR: Record<AlarmTier, string> = {
  1: 'bg-[var(--color-tier-1)]',
  2: 'bg-[var(--color-tier-2)]',
  3: 'bg-[var(--color-tier-3)]',
  4: 'bg-[var(--color-tier-4)]',
  5: 'bg-[var(--color-tier-5)]',
};

const DEFAULT_LABELS: AlarmCardLabels = {
  tiers: { 1: 'Critical', 2: 'Intrusion', 3: 'Test', 4: 'Info', 5: 'Maintenance' },
  sources: { panel: 'Panel', ai: 'AI', nvr: 'NVR', manual: 'Manual' },
  claimed: 'Claimed',
  claimedBy: (name) => `Claimed by ${name}`,
  preAlarm: 'Pre',
};

/**
 * Alarm card — primary affordance in the operator queue.
 *
 * Design language:
 *   - flat surface, hairline border, sharp 3px corners (instrument panel,
 *     not Material card)
 *   - tier indicator is a 4px solid block on the leading edge — readable
 *     across the room from the wallboard
 *   - elapsed time is large monospace tabular nums (the data the operator
 *     scans first)
 *   - selected state: amber leading rule + tinted background + ring
 *
 * Renders as `<button>` so it lives in the focus order.
 */
const AlarmCard = React.forwardRef<HTMLButtonElement, AlarmCardProps>(
  (
    {
      tier,
      siteName,
      cameraLabel,
      source,
      description,
      receivedAt,
      confidence,
      thumbnailUrl,
      emptyThumbnailLabel,
      referenceLabel,
      tenant,
      claimedBy,
      selected = false,
      state,
      labels,
      now,
      className,
      ...props
    },
    ref,
  ) => {
    const text = { ...DEFAULT_LABELS, ...labels };
    const elapsed = formatElapsed(receivedAt, now ?? new Date());
    return (
      <button
        ref={ref}
        type="button"
        aria-current={selected ? 'true' : undefined}
        className={cn(
          'group relative grid w-full grid-cols-[4px_auto_1fr] gap-3 rounded-sm',
          'border bg-[var(--color-surface)] text-start transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-canvas)]',
          selected
            ? 'border-[var(--color-primary)] bg-[var(--color-warning-tint)]'
            : 'border-[var(--color-hairline)] hover:border-[var(--color-hairline-strong)] hover:bg-[var(--color-surface-2)]',
          state?.muted && !selected && 'opacity-70',
          className,
        )}
        {...props}
      >
        {/* Leading tier rail — 1px hairline in the tier colour (tokens ban thicker side stripes) */}
        <span aria-hidden className={cn('h-full w-px', TIER_COLOR[tier])} />

        {/* Thumbnail — sharp corners, hairline */}
        <div className="relative my-2 h-[68px] w-[112px] shrink-0 overflow-hidden bg-[var(--color-surface-2)] border border-[var(--color-hairline)]">
          {thumbnailUrl ? (
            <img src={thumbnailUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div
              role={emptyThumbnailLabel ? 'img' : undefined}
              aria-label={emptyThumbnailLabel}
              aria-hidden={emptyThumbnailLabel ? undefined : true}
              className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center text-[var(--color-foreground-mute)]"
            >
              <CamGlyph />
              {emptyThumbnailLabel ? (
                <span className="text-[0.5625rem] leading-tight">{emptyThumbnailLabel}</span>
              ) : null}
            </div>
          )}
          {thumbnailUrl ? (
            <div className="absolute start-1 top-1">
              <LiveBadge label={text.preAlarm} />
            </div>
          ) : null}
        </div>

        {/* Content column */}
        <div className="min-w-0 grid grid-cols-1 gap-1.5 py-2 pe-3">
          {/* Row 1 — meta strip */}
          <div className="flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.16em] tabular-nums">
            <span className="text-[var(--color-foreground)]">
              {TIER_LABEL[tier]} · {text.tiers[tier]}
            </span>
            <span className="text-[var(--color-foreground-mute)]">·</span>
            <span className="text-[var(--color-foreground-3)]">{text.sources[source]}</span>
            {claimedBy && (
              <>
                <span className="text-[var(--color-foreground-mute)]">·</span>
                <span className="text-[var(--color-amber-500)]" title={text.claimedBy(claimedBy)}>
                  {text.claimed}
                </span>
              </>
            )}
            {state ? (
              <span className="rounded-sm border border-[var(--color-hairline-strong)] px-1 text-[var(--color-foreground-2)]">
                {state.label}
              </span>
            ) : null}
            {referenceLabel ? (
              <span
                className="max-w-28 truncate text-[var(--color-foreground-mute)]"
                title={referenceLabel}
              >
                {referenceLabel}
              </span>
            ) : null}
            <span className="ms-auto text-[0.875rem] font-medium normal-case text-[var(--color-foreground)] tracking-tight">
              <span title={receivedAt.toISOString()}>{elapsed}</span>
            </span>
          </div>

          {/* Row 2 — description */}
          <p className="line-clamp-2 text-[0.875rem] leading-snug text-[var(--color-foreground)]">
            {description}
          </p>

          {/* Row 3 — site/camera location */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-[var(--color-foreground-3)]">
            <span className="inline-flex items-center gap-1">
              <PinGlyph /> {siteName}
            </span>
            {cameraLabel && (
              <span className="inline-flex items-center gap-1">
                <CamGlyph small /> {cameraLabel}
              </span>
            )}
          </div>

          {/* Row 4 — tenant + AI confidence */}
          {(tenant || typeof confidence === 'number') && (
            <div className="flex items-center gap-3 pt-0.5">
              {tenant && <TenantBadge tier={tenant.tier} name={tenant.name} />}
              {typeof confidence === 'number' && (
                <ConfidenceBar score={confidence} className="flex-1" />
              )}
            </div>
          )}
        </div>
      </button>
    );
  },
);
AlarmCard.displayName = 'AlarmCard';

/** Elapsed time as a short unit in the page language: "36 sec", "4 min", "2 hr". */
function formatElapsed(receivedAt: Date, now: Date): string {
  const seconds = Math.max(0, Math.round((now.getTime() - receivedAt.getTime()) / 1000));
  const [unit, value] =
    seconds < 60
      ? (['second', seconds] as const)
      : seconds < 3600
        ? (['minute', Math.round(seconds / 60)] as const)
        : (['hour', Math.round(seconds / 3600)] as const);
  const locale = typeof document === 'undefined' ? undefined : document.documentElement.lang;
  return new Intl.NumberFormat(locale || undefined, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
  }).format(value);
}

function CamGlyph({ small = false }: { small?: boolean }) {
  const sz = small ? 11 : 16;
  return (
    <svg
      viewBox="0 0 20 20"
      width={sz}
      height={sz}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="6" width="11" height="9" rx="0.5" />
      <path d="M14 9l3-2v8l-3-2" />
      <circle cx="8" cy="10.5" r="2" />
    </svg>
  );
}

function PinGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="11"
      height="11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 17s5-4.5 5-9a5 5 0 0 0-10 0c0 4.5 5 9 5 9z" />
      <circle cx="10" cy="8" r="1.75" />
    </svg>
  );
}

export { AlarmCard };
